import InputMask from "react-input-mask";
import React, { useCallback } from "react";
import { cleanPhoneNumber } from "../utils/phone";

export type PhoneInputProps = Partial<{
  value: string;
  name: string;
  autoComplete: boolean | string;
  onChange: (val: string) => any;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => any;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => any;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => any;
}>;

export default function PhoneInput(props: PhoneInputProps) {
  const autoCompleteValue =
    typeof props.autoComplete === "string" ? props.autoComplete : props.autoComplete ? "on" : "off";

  const handleChange = useCallback(
    (value: string) => {
      if (props.onChange) props.onChange(cleanPhoneNumber(value));
    },
    [props.autoComplete, props.onChange]
  );

  // Force send back to start so we don't start at the end of the mask if empty
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!props.value || props.value === "+1") {
        e.target.setSelectionRange(0, 0);
      }
      if (props.onFocus) props.onFocus(e);
    },
    [props.onFocus, props.value]
  );

  const handleAutocompleteChange = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { value } = e.target;
      let newValue = value;

      // If we only have 10 digits, we probably don't have the leading "1"
      if (newValue.length === 10) {
        newValue = `1${newValue}`;
      }

      // If we have 11 numbers, we need the leading "+"
      if (newValue.length === 11) {
        newValue = `+${newValue}`;
      }
      handleChange(newValue);
      handleFocus(e);
    },
    [handleChange, handleFocus]
  );

  return (
    <>
      {/* Silly hack to get the select input to autofill correctly */}
      {props.autoComplete !== "off" && (
        <input
          tabIndex={-1}
          onChange={handleAutocompleteChange}
          type="text"
          style={{
            opacity: 0,
            position: "fixed",
            top: "-1000px",
            pointerEvents: "none",
          }}
          autoComplete={autoCompleteValue}
          value={props.value}
        />
      )}
      <InputMask
        data-name={props.name}
        autoComplete={autoCompleteValue}
        data-lpignore={props.autoComplete ? "" : true}
        alwaysShowMask
        mask="+1 (999) 999-9999"
        type="tel"
        value={props.value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={props.onBlur}
        onKeyDown={props.onKeyDown}
        onFocus={handleFocus}
      />
    </>
  );
}
